import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import UnrankedFactionSelect from './UnrankedFactionSelect';
import UnrankedMapSelect from './UnrankedMapSelect';

interface TournamentFormData {
  name: string;
  description: string;
  tournament_type: string;
  tournament_mode: 'ranked' | 'unranked' | 'team';
  max_participants: number | null;
  round_duration_days: number;
  auto_advance_round: boolean;
  general_rounds: number;
  final_rounds: number;
  general_rounds_format: 'bo1' | 'bo3' | 'bo5';
  final_rounds_format: 'bo1' | 'bo3' | 'bo5';
  started_at?: string;
}

interface TournamentFormProps {
  mode: 'create' | 'edit';
  formData: TournamentFormData;
  onFormDataChange: (data: TournamentFormData) => void;
  onSubmit: (e: React.FormEvent) => void;
  unrankedFactions: string[];
  onUnrankedFactionsChange: (factionIds: string[]) => void;
  unrankedMaps: string[];
  onUnrankedMapsChange: (mapIds: string[]) => void;
  isLoading?: boolean;
  onCancel?: () => void;
}

const TournamentForm: React.FC<TournamentFormProps> = ({
  mode,
  formData,
  onFormDataChange,
  onSubmit,
  unrankedFactions,
  onUnrankedFactionsChange,
  unrankedMaps,
  onUnrankedMapsChange,
  isLoading = false,
  onCancel,
}) => {
  const { t } = useTranslation();

  // Determine tournament type options based on mode and status
  const canConfigureRounds = () => {
    if (mode === 'create') return true;
    // In edit mode, only allow if tournament is not started
    return true; // You can add status check if needed
  };

  const handleTournamentTypeChange = (newType: string) => {
    const updatedData = { ...formData, tournament_type: newType };

    // Reset round values based on new format to their creation defaults
    if (newType === 'elimination') {
      updatedData.general_rounds_format = 'bo3';
      updatedData.final_rounds_format = 'bo5';
      updatedData.general_rounds = 0;
      updatedData.final_rounds = 0;
    } else if (newType === 'league') {
      updatedData.general_rounds = 1;
      updatedData.final_rounds = 0;
      updatedData.general_rounds_format = 'bo3';
    } else if (newType === 'swiss') {
      updatedData.general_rounds = 1;
      updatedData.final_rounds = 0;
      updatedData.general_rounds_format = 'bo3';
    } else if (newType === 'swiss_elimination') {
      updatedData.general_rounds = 1;
      updatedData.final_rounds = 1;
      updatedData.general_rounds_format = 'bo3';
      updatedData.final_rounds_format = 'bo5';
    }

    onFormDataChange(updatedData);
  };

  return (
    <form id="tournament-form" className="bg-white rounded-lg shadow-md p-8 space-y-6" onSubmit={onSubmit}>
      {/* SECTION 1: BASIC INFORMATION */}
      <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-white">
        <h3 className="mb-4 font-semibold text-gray-800">{t('tournament.basic_info', 'Basic Information')}</h3>
        
        {/* Tournament Name - Full width */}
        <div className="mb-4 flex flex-col gap-2">
          <label className="font-medium text-gray-700">{t('tournament_name', 'Tournament Name')}</label>
          <input
            type="text"
            placeholder={t('tournament_name', 'Tournament Name')}
            value={formData.name}
            onChange={(e) => onFormDataChange({ ...formData, name: e.target.value })}
            required
            disabled={isLoading || (mode === 'edit')}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
          />
        </div>

        {/* Tournament Description - Full width */}
        <div className="mb-4 flex flex-col gap-2">
          <label className="font-medium text-gray-700">{t('tournament_description', 'Description')}</label>
          <textarea
            placeholder={t('tournament_description', 'Description')}
            value={formData.description}
            onChange={(e) => onFormDataChange({ ...formData, description: e.target.value })}
            rows={10}
            required
            disabled={isLoading}
            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100 resize-vertical"
          />
        </div>
        
        {/* Tournament Mode Selector (Ranked/Unranked/Team) */}
        <div className="flex flex-col gap-2">
          <label className="font-medium text-gray-700">{t('tournament.match_type', 'Match Type')}:</label>
          <div className="flex flex-col gap-2">
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                value="ranked"
                checked={formData.tournament_mode === 'ranked'}
                onChange={(e) => onFormDataChange({ ...formData, tournament_mode: e.target.value as any })}
                disabled={isLoading || mode === 'edit'}
              />
              {t('tournament.ranked', 'Ranked (1v1, ELO impact)')}
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                value="unranked"
                checked={formData.tournament_mode === 'unranked'}
                onChange={(e) => onFormDataChange({ ...formData, tournament_mode: e.target.value as any })}
                disabled={isLoading || mode === 'edit'}
              />
              {t('tournament.unranked', 'Unranked (1v1, no ELO)')}
            </label>
            <label className="flex items-center gap-2 cursor-pointer">
              <input
                type="radio"
                value="team"
                checked={formData.tournament_mode === 'team'}
                onChange={(e) => onFormDataChange({ ...formData, tournament_mode: e.target.value as any })}
                disabled={isLoading || mode === 'edit'}
              />
              {t('tournament.team', 'Team (2v2, no ELO)')}
            </label>
          </div>
        </div>
      </div>

      {/* SECTION 2: TOURNAMENT TYPE AND PARTICIPANTS */}
      <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-white">
        <h3 className="mb-4 font-semibold text-gray-800">{t('tournament.format_settings', 'Format Settings')}</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex flex-col gap-2">
            <label className="font-medium text-gray-700">{t('tournament.tournament_format', 'Tournament Format')}</label>
            <select
              value={formData.tournament_type}
              onChange={(e) => handleTournamentTypeChange(e.target.value)}
              required
              disabled={isLoading}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            >
              <option value="elimination">{t('option_type_elimination', 'Elimination')}</option>
              <option value="league">{t('option_type_league', 'League')}</option>
              <option value="swiss">{t('option_type_swiss', 'Swiss')}</option>
              <option value="swiss_elimination">{t('option_type_swiss_elimination', 'Swiss-Elimination Mix')}</option>
            </select>
          </div>
          <div className="flex flex-col gap-2">
            <label className="font-medium text-gray-700">{t('label_max_participants', 'Max Participants')}</label>
            <input
              type="number"
              placeholder={t('label_max_participants', 'Max Participants')}
              min="2"
              max="256"
              value={formData.max_participants || ''}
              onChange={(e) => onFormDataChange({ 
                ...formData, 
                max_participants: e.target.value ? parseInt(e.target.value) : null 
              })}
              disabled={isLoading}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
          </div>
        </div>
      </div>

      {/* SECTION 3: UNRANKED ASSETS (conditional) */}
      {formData.tournament_mode === 'unranked' && (
        <div className="mb-6">
          <h3>{t('tournament.unranked_assets', 'Unranked Tournament Assets')}</h3>
          <p className="text-sm text-gray-600 italic mb-4">{t('tournament.select_allowed_factions_maps', 'Select which factions and maps are allowed in this tournament')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <UnrankedFactionSelect 
              tournamentId={undefined}
              selectedFactionIds={unrankedFactions}
              onChange={onUnrankedFactionsChange}
              disabled={isLoading}
            />
            <UnrankedMapSelect 
              tournamentId={undefined}
              selectedMapIds={unrankedMaps}
              onChange={onUnrankedMapsChange}
              disabled={isLoading}
            />
          </div>
        </div>
      )}

      {/* SECTION 3B: TEAM ASSETS (same as unranked) */}
      {formData.tournament_mode === 'team' && (
        <div className="mb-6">
          <h3>{t('tournament.team_assets', 'Team Tournament Assets')}</h3>
          <p className="text-sm text-gray-600 italic mb-4">{t('tournament.select_allowed_factions_maps', 'Select which factions and maps are allowed in this tournament')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <UnrankedFactionSelect 
              tournamentId={undefined}
              selectedFactionIds={unrankedFactions}
              onChange={onUnrankedFactionsChange}
              disabled={isLoading}
            />
            <UnrankedMapSelect 
              tournamentId={undefined}
              selectedMapIds={unrankedMaps}
              onChange={onUnrankedMapsChange}
              disabled={isLoading}
            />
          </div>
        </div>
      )}

      {/* SECTION 3C: RANKED ASSETS (only ranked factions/maps) */}
      {formData.tournament_mode === 'ranked' && (
        <div className="mb-6">
          <h3>{t('tournament.ranked_assets', 'Ranked Tournament Assets')}</h3>
          <p className="text-sm text-gray-600 italic mb-4">{t('tournament.select_allowed_ranked_factions_maps', 'Select which ranked factions and maps are allowed in this tournament')}</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            <UnrankedFactionSelect 
              tournamentId={undefined}
              selectedFactionIds={unrankedFactions}
              onChange={onUnrankedFactionsChange}
              disabled={isLoading}
              isRankedOnly={true}
            />
            <UnrankedMapSelect 
              tournamentId={undefined}
              selectedMapIds={unrankedMaps}
              onChange={onUnrankedMapsChange}
              disabled={isLoading}
              isRankedOnly={true}
            />
          </div>
        </div>
      )}

      <div className="mb-6 p-4 border border-gray-200 rounded-lg bg-white">
        <div className="mb-4">
          <h3 className="font-semibold text-gray-800">{t('tournament.round_configuration', 'Round Configuration')}</h3>
          {!formData.max_participants && (
            <span className="text-sm text-gray-600 italic">{t('tournaments.round_config_optional', 'Optional - set when preparing the tournament')}</span>
          )}
        </div>

        <div className="flex flex-col md:flex-row items-start md:items-end gap-4 mb-4">
          <div className="flex-1">
            <label className="block font-medium text-gray-700 mb-2">{t('label_round_duration', 'Round Duration (days)')}</label>
            <input
              type="number"
              min="1"
              max="365"
              value={formData.round_duration_days}
              onChange={(e) => onFormDataChange({ 
                ...formData, 
                round_duration_days: parseInt(e.target.value) 
              })}
              disabled={isLoading}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
          </div>

          <div className="flex items-center gap-3">
            <label className="font-medium text-gray-700">{t('label_auto_advance_rounds', 'Auto-advance Rounds')}</label>
            <input
              type="checkbox"
              checked={formData.auto_advance_round}
              onChange={(e) => onFormDataChange({ 
                ...formData, 
                auto_advance_round: e.target.checked 
              })}
              className="w-5 h-5"
              disabled={isLoading}
            />
          </div>
        </div>

        {/* Edit mode: Show started_at field */}
        {mode === 'edit' && (
          <div className="flex flex-col gap-2 mt-4">
            <label className="font-medium text-gray-700">{t('label_tournament_start_date', 'Tournament Start Date')}</label>
            <input
              type="date"
              value={formData.started_at ? new Date(formData.started_at).toISOString().split('T')[0] : ''}
              onChange={(e) => onFormDataChange({ ...formData, started_at: e.target.value })}
              disabled={isLoading}
              className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
            />
          </div>
        )}

        {/* ELIMINATION TOURNAMENT - Auto-calculated rounds */}
        {canConfigureRounds() && formData.tournament_type === 'elimination' && (
          <div className="border-t border-gray-200 pt-6">
            <h4 className="font-semibold text-gray-800 mb-2">{t('tournament.round_configuration', 'Round Configuration')}</h4>
            <p className="text-sm text-gray-600 mb-4">{t('tournament.configure_match_formats_elimination', 'Configure match formats for your elimination tournament')}</p>
            
            <div className="border border-blue-200 bg-blue-50 p-4 rounded mb-4">
              <p className="text-sm text-blue-900">ℹ️ {t('tournament.elimination_auto_calculated', 'Tournament rounds are automatically calculated based on the number of participants.')}</p>
            </div>

            <div className="space-y-4">
              <div className="flex flex-col gap-2">
                <label className="font-medium text-gray-700">{t('tournament.preliminary_rounds_format', 'Preliminary Rounds Match Format')}</label>
                <select
                  value={formData.general_rounds_format}
                  onChange={(e) => onFormDataChange({
                    ...formData,
                    general_rounds_format: e.target.value as 'bo1' | 'bo3' | 'bo5'
                  })}
                  disabled={isLoading}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="bo1">{t('match_format.bo1', 'Best of 1 (Single match)')}</option>
                  <option value="bo3">{t('match_format.bo3', 'Best of 3 (First to 2 wins)')}</option>
                  <option value="bo5">{t('match_format.bo5', 'Best of 5 (First to 3 wins)')}</option>
                </select>
                <small className="text-gray-600">{t('tournament.preliminary_format_help', 'Best of format for all preliminary elimination rounds')}</small>
              </div>

              <div className="flex flex-col gap-2">
                <label className="font-medium text-gray-700">{t('tournament.final_match_format', 'Final Match Format')}</label>
                <select
                  value={formData.final_rounds_format}
                  onChange={(e) => onFormDataChange({
                    ...formData,
                    final_rounds_format: e.target.value as 'bo1' | 'bo3' | 'bo5'
                  })}
                  disabled={isLoading}
                  className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                >
                  <option value="bo1">{t('match_format.bo1', 'Best of 1 (Single match)')}</option>
                  <option value="bo3">{t('match_format.bo3', 'Best of 3 (First to 2 wins)')}</option>
                  <option value="bo5">{t('match_format.bo5', 'Best of 5 (First to 3 wins)')}</option>
                </select>
                <small className="text-gray-600">{t('tournament.final_format_help', 'Best of format for the final match')}</small>
              </div>
            </div>
          </div>
        )}

        {/* NON-ELIMINATION TOURNAMENT - Manual round configuration */}
        {canConfigureRounds() && formData.tournament_type !== 'elimination' && (
          <div className="border-t border-gray-200 pt-6 space-y-6">
            {/* LEAGUE TOURNAMENT */}
            {formData.tournament_type === 'league' && (
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">{t('tournament.league_configuration', 'League Format Configuration')}</h4>
                <p className="text-sm text-gray-600 mb-4">{t('tournament.league_description', 'Configure the League tournament format')}</p>
                <div className="space-y-4">
                  <div className="flex flex-col gap-2">
                    <label className="font-medium text-gray-700">{t('tournament.league_format', 'League Format')}</label>
                    <select
                      value={formData.general_rounds}
                      onChange={(e) => onFormDataChange({
                        ...formData,
                        general_rounds: parseInt(e.target.value),
                      })}
                      disabled={isLoading}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    >
                      <option value="1">{t('tournament.single_round', 'Single Wave')}</option>
                      <option value="2">{t('tournament.double_round', 'Double Wave')}</option>
                    </select>
                    <small className="text-gray-600">{t('tournament.league_format_help', 'Select whether teams play once or twice against each other')}</small>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="font-medium text-gray-700">{t('tournament.match_format', 'Match Format')}</label>
                    <select
                      value={formData.general_rounds_format}
                      onChange={(e) => onFormDataChange({
                        ...formData,
                        general_rounds_format: e.target.value as 'bo1' | 'bo3' | 'bo5'
                      })}
                      disabled={isLoading}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    >
                      <option value="bo1">{t('match_format.bo1', 'Best of 1 (Single match)')}</option>
                      <option value="bo3">{t('match_format.bo3', 'Best of 3 (First to 2 wins)')}</option>
                      <option value="bo5">{t('match_format.bo5', 'Best of 5 (First to 3 wins)')}</option>
                    </select>
                    <small className="text-gray-600">{t('tournament.match_format_help', 'Number of games in each match')}</small>
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-gray-200">
                    <p className="text-sm"><strong>{t('tournament.format', 'Format')}:</strong> {formData.general_rounds === 2 ? t('tournament.double_round', 'Double Wave') : t('tournament.single_round', 'Single Wave')} ({formData.general_rounds_format?.toUpperCase()})</p>
                  </div>
                </div>
              </div>
            )}

            {/* SWISS TOURNAMENT */}
            {formData.tournament_type === 'swiss' && (
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">{t('tournament.swiss_configuration', 'Swiss Rounds Configuration')}</h4>
                <p className="text-sm text-gray-600 mb-4">{t('tournament.swiss_description', 'Configure the Swiss round tournament')}</p>
                <div className="space-y-4">
                  <div className="flex flex-col gap-2">
                    <label className="font-medium text-gray-700">{t('tournament.number_swiss_rounds', 'Number of Swiss Rounds')}</label>
                    <input
                      type="number"
                      min="1"
                      max="20"
                      value={formData.general_rounds}
                      onChange={(e) => onFormDataChange({
                        ...formData,
                        general_rounds: parseInt(e.target.value),
                      })}
                      disabled={isLoading}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    />
                    <small className="text-gray-600">{t('tournament.swiss_rounds_help', 'Number of Swiss system rounds to run (typically 3-7 rounds for Swiss tournaments)')}</small>
                  </div>
                  <div className="flex flex-col gap-2">
                    <label className="font-medium text-gray-700">{t('tournament.match_format', 'Match Format')}</label>
                    <select
                      value={formData.general_rounds_format}
                      onChange={(e) => onFormDataChange({
                        ...formData,
                        general_rounds_format: e.target.value as 'bo1' | 'bo3' | 'bo5'
                      })}
                      disabled={isLoading}
                      className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                    >
                      <option value="bo1">{t('match_format.bo1', 'Best of 1 (Single match)')}</option>
                      <option value="bo3">{t('match_format.bo3', 'Best of 3 (First to 2 wins)')}</option>
                      <option value="bo5">{t('match_format.bo5', 'Best of 5 (First to 3 wins)')}</option>
                    </select>
                    <small className="text-gray-600">{t('tournament.match_format_help', 'Number of games in each match')}</small>
                  </div>
                  <div className="bg-gray-50 p-3 rounded border border-gray-200">
                    <p className="text-sm"><strong>{t('tournament.total_rounds', 'Total Rounds')}:</strong> {formData.general_rounds} {t('tournament.swiss_rounds', 'Swiss rounds')} ({formData.general_rounds_format?.toUpperCase()})</p>
                  </div>
                </div>
              </div>
            )}

            {/* SWISS-ELIMINATION HYBRID TOURNAMENT */}
            {formData.tournament_type === 'swiss_elimination' && (
              <div>
                <h4 className="font-semibold text-gray-800 mb-2">{t('tournament.swiss_elimination_configuration', 'Swiss-Elimination Mix Configuration')}</h4>
                <p className="text-sm text-gray-600 mb-4">{t('tournament.swiss_elimination_description', 'Configure Swiss qualifying rounds and elimination bracket with different match formats')}</p>
                <div className="border border-blue-200 bg-blue-50 p-4 rounded mb-4">
                  <p className="text-sm text-blue-900">ℹ️ {t('tournament.swiss_elimination_info', 'This tournament combines a Swiss phase for qualification with an elimination phase for final ranking. You can set different match formats for qualification and the grand final.')}</p>
                </div>
                
                {/* Rounds Configuration */}
                <div className="border border-gray-200 p-4 rounded-lg mb-4">
                  <h5 className="font-medium text-gray-800 mb-4">{t('tournament.rounds_configuration', 'Rounds Configuration')}</h5>
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div className="flex flex-col gap-2">
                      <label className="font-medium text-gray-700">{t('tournament.number_swiss_rounds', 'Number of Swiss Rounds')}</label>
                      <input
                        type="number"
                        min="1"
                        max="20"
                        value={formData.general_rounds}
                        onChange={(e) => onFormDataChange({
                          ...formData,
                          general_rounds: parseInt(e.target.value),
                        })}
                        disabled={isLoading}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      />
                      <small className="text-gray-600">{t('tournament.qualifying_rounds_help', 'Qualifying rounds using Swiss system')}</small>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="font-medium text-gray-700">{t('tournament.number_elimination_rounds', 'Number of Elimination Rounds')}</label>
                      <input
                        type="number"
                        min="1"
                        max="10"
                        value={formData.final_rounds}
                        onChange={(e) => onFormDataChange({
                          ...formData,
                          final_rounds: parseInt(e.target.value),
                        })}
                        disabled={isLoading}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      />
                      <small className="text-gray-600">{t('tournament.elimination_rounds_help', 'Total elimination rounds (includes grand final)')}</small>
                    </div>
                  </div>
                </div>

                {/* Match Formats */}
                <div className="border border-gray-200 p-4 rounded-lg mb-4">
                  <h5 className="font-medium text-gray-800 mb-4">{t('tournament.match_formats', 'Match Formats')}</h5>
                  <div className="space-y-4">
                    <div className="flex flex-col gap-2">
                      <label className="font-medium text-gray-700">{t('tournament.general_format', 'General Format (Swiss Rounds + Elimination except Final)')}</label>
                      <select
                        value={formData.general_rounds_format}
                        onChange={(e) => onFormDataChange({
                          ...formData,
                          general_rounds_format: e.target.value as 'bo1' | 'bo3' | 'bo5'
                        })}
                        disabled={isLoading}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      >
                        <option value="bo1">{t('match_format.bo1', 'Best of 1 (Single match)')}</option>
                        <option value="bo3">{t('match_format.bo3', 'Best of 3 (First to 2 wins)')}</option>
                        <option value="bo5">{t('match_format.bo5', 'Best of 5 (First to 3 wins)')}</option>
                      </select>
                      <small className="text-gray-600">{t('tournament.general_format_help', 'Used for Swiss rounds and all elimination rounds except the grand final')}</small>
                    </div>
                    <div className="flex flex-col gap-2">
                      <label className="font-medium text-gray-700">{t('tournament.final_format', 'Final Format (Grand Final)')}</label>
                      <select
                        value={formData.final_rounds_format}
                        onChange={(e) => onFormDataChange({
                          ...formData,
                          final_rounds_format: e.target.value as 'bo1' | 'bo3' | 'bo5'
                        })}
                        disabled={isLoading}
                        className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 disabled:bg-gray-100"
                      >
                        <option value="bo1">{t('match_format.bo1', 'Best of 1 (Single match)')}</option>
                        <option value="bo3">{t('match_format.bo3', 'Best of 3 (First to 2 wins)')}</option>
                        <option value="bo5">{t('match_format.bo5', 'Best of 5 (First to 3 wins)')}</option>
                      </select>
                      <small className="text-gray-600">{t('tournament.final_format_help', 'Used only for the grand final match')}</small>
                    </div>
                  </div>
                </div>

                {/* Summary */}
                <div className="bg-gray-50 p-4 rounded border border-gray-200">
                  <p className="font-medium text-gray-800 mb-2">{t('tournament.tournament_structure', 'Tournament Structure')}:</p>
                  <div className="space-y-1 text-sm text-gray-700">
                    <p>• {t('tournament.swiss_phase', 'Swiss Phase')}: {formData.general_rounds} {t('tournament.rounds', 'rounds')} ({formData.general_rounds_format?.toUpperCase()})</p>
                    {formData.final_rounds > 1 && (
                      <p>• {t('tournament.qualification_phase', 'Qualification Phase')}:  {formData.final_rounds - 1} {t('tournament.rounds', 'rounds')} ({formData.general_rounds_format?.toUpperCase()}) [{t('tournament.quarters_semis', 'Quarters, Semis, etc')}]</p>
                    )}
                    <p>• {t('tournament.grand_final', 'Grand Final')}:  1 {t('tournament.round', 'round')} ({formData.final_rounds_format?.toUpperCase()})</p>
                    <p><strong>{t('tournament.total_rounds', 'Total Rounds')}:</strong> {formData.general_rounds + formData.final_rounds}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex w-full gap-2">
        <button type="submit" className="bg-blue-600 hover:bg-blue-700 text-white font-semibold py-2 px-4 rounded flex-1 disabled:opacity-50 disabled:cursor-not-allowed" disabled={isLoading}>
          {isLoading ? t('loading') : (mode === 'create' ? t('tournament_create') : t('btn_confirm'))}
        </button>
        {mode === 'edit' && onCancel && (
          <button 
            type="button"
            onClick={onCancel}
            className="bg-gray-500 hover:bg-gray-600 text-white font-semibold py-2 px-4 rounded flex-1"
          >
            {t('btn_cancel', 'Cancel')}
          </button>
        )}
      </div>
    </form>
  );
};

export default TournamentForm;
